package pl.nickers.match3.ecs

import pl.nickers.match3.SoundEvent

internal class EffectResolveSystem(
    private val pieceSet: PieceSet = PieceSetLoader.default(),
) : BaseSystem() {
    private lateinit var posMapper: ComponentMapper<GridPositionComponent>
    private lateinit var explodingMapper: ComponentMapper<ExplodingComponent>
    private lateinit var fullBoardMapper: ComponentMapper<FullBoardExplosionComponent>
    private lateinit var bombFaceMapper: ComponentMapper<BombFaceComponent>
    private lateinit var ghostBombMapper: ComponentMapper<GhostBombComponent>
    private lateinit var bodyImageMapper: ComponentMapper<BodyImageComponent>
    private lateinit var draggableMapper: ComponentMapper<DraggableComponent>
    private lateinit var activatedSwirlMapper: ComponentMapper<ActivatedSwirlComponent>
    private lateinit var swirlVacuumMapper: ComponentMapper<SwirlVacuumComponent>

    override fun initialize() {
        posMapper = world.mapper()
        explodingMapper = world.mapper()
        fullBoardMapper = world.mapper()
        bombFaceMapper = world.mapper()
        ghostBombMapper = world.mapper()
        bodyImageMapper = world.mapper()
        draggableMapper = world.mapper()
        activatedSwirlMapper = world.mapper()
        swirlVacuumMapper = world.mapper()
    }

    override fun processSystem() {
        val board = world.boardState() ?: return
        if (board.phase != GamePhase.RESOLVE || board.currentSubphase != ResolveSubphase.EFFECT) return
        val exploding = world.getEntitiesForAspect(Aspect.all(ExplodingComponent::class)).toList()
        if (exploding.isEmpty()) return

        val (swirls, bombs) = exploding.partition { world.isFamily(it, pieceSet, Family.SWIRL) }
        if (swirls.isNotEmpty()) {
            resolveSwirlActivations(swirls, board)
        }
        if (bombs.isNotEmpty()) {
            resolveEffects(bombs, board)
        }
        board.didWork = true
    }

    /**
     * Clears every regular jelly on the board whose piece id matches the swirl's
     * [ActivatedSwirlComponent.targetPieceId]. The swirl entity itself is consumed
     * by detonation: its [ExplodingComponent], [ActivatedSwirlComponent], and the
     * entity itself are removed.
     *
     * Defensive fallback: a missing [ActivatedSwirlComponent] uses
     * [World.mostCommonJellyPieceId] (theoretically unreachable given the upstream wiring).
     */
    private fun resolveSwirlActivations(swirls: List<Int>, board: BoardStateComponent) {
        val jellyAspect = Aspect.all(GridPositionComponent::class, JellyTypeComponent::class, DraggableComponent::class)
        for (swirl in swirls) {
            val swirlPos = posMapper[swirl] ?: continue
            val targetPieceId = activatedSwirlMapper[swirl]?.targetPieceId
                ?: world.mostCommonJellyPieceId(pieceSet)
            val targets = if (targetPieceId != null) {
                world.getEntitiesForAspect(jellyAspect).filter { entityId ->
                    entityId != swirl &&
                        world.isFamily(entityId, pieceSet, Family.JELLY) &&
                        world.pieceIdOf(entityId) == targetPieceId
                }.toSet()
            } else {
                emptySet()
            }

            if (targets.isEmpty()) {
                // Nothing to animate — clean up immediately.
                explodingMapper.remove(swirl)
                activatedSwirlMapper.remove(swirl)
                world.deleteEntity(swirl)
                continue
            }

            // Mark targets for the vacuum animation; actual deletion happens in
            // GameViewModel.onSwirlVacuumAnimationFinished() once the animation ends.
            targets.forEach { entityId ->
                swirlVacuumMapper.set(entityId, SwirlVacuumComponent(
                    swirlEntityId = swirl,
                    targetRow = swirlPos.row,
                    targetCol = swirlPos.col,
                ))
            }
            // Remove activation markers; swirl entity stays alive until vacuum animation ends.
            explodingMapper.remove(swirl)
            activatedSwirlMapper.remove(swirl)
        }
    }

    private fun resolveEffects(exploding: List<Int>, board: BoardStateComponent) {
        val aspect = Aspect.all(GridPositionComponent::class, JellyTypeComponent::class)
        val isFullBoard = exploding.any { fullBoardMapper.has(it) }
        val inRange: Set<Int>
        val justDetonated = mutableSetOf<Int>()
        if (isFullBoard) {
            exploding.forEach {
                explodingMapper.remove(it)
                fullBoardMapper.remove(it)
                justDetonated.add(it)
            }
            inRange = world.getEntitiesForAspect(aspect).toSet()
        } else {
            val collected = mutableSetOf<Int>()
            for (bombEntity in exploding) {
                val ghostComp = ghostBombMapper[bombEntity]
                val pos = if (ghostComp != null) {
                    GridPositionComponent(ghostComp.row, ghostComp.col)
                } else {
                    posMapper[bombEntity] ?: continue
                }
                explodingMapper.remove(bombEntity)
                justDetonated.add(bombEntity)
                for (dr in -1..1) {
                    for (dc in -1..1) {
                        val r = pos.row + dr
                        val c = pos.col + dc
                        if (r in 0 until board.height && c in 0 until board.width) {
                            world.findEntityAt(r, c)?.let { collected.add(it) }
                        }
                    }
                }
            }
            inRange = collected
        }

        val consumedMapper: ComponentMapper<ConsumedComponent> = world.mapper()
        justDetonated.forEach { consumedMapper.set(it, ConsumedComponent()) }

        val toRemove = resolveAreaOfEffect(
            world = world,
            targets = inRange,
            pieceSet = pieceSet,
        )

        val ghostsJustDetonated = justDetonated.filter { ghostBombMapper.has(it) }.toSet()

        val crossBombsToGhost = justDetonated.filter {
            world.hasModifier(it, pieceSet, Modifier.GHOST_REPEAT) && !ghostBombMapper.has(it)
        }.toSet()
        for (entity in crossBombsToGhost) {
            val pos = posMapper[entity]!!
            posMapper.remove(entity)
            bodyImageMapper.remove(entity)
            bombFaceMapper.remove(entity)
            draggableMapper.remove(entity)
            ghostBombMapper.set(entity, GhostBombComponent(pos.row, pos.col))
        }

        for (entity in ghostsJustDetonated) {
            world.deleteEntity(entity)
        }

        val finalRemovals = toRemove - crossBombsToGhost
        world.scoreDeleteAndCheckGoals(finalRemovals, board)
    }
}
